import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { CreditTierUpdateStore } from "../../store";
import { InputString } from "../../../common/controls";
import { InputNumber } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type CreditTierUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  name_es: string;
  name_esSelectedValue?: { displayId: string; id: string } | null;
  onName_esChange?: (value: string) => void;
  name_esError?: string;
  name_en: string;
  name_enSelectedValue?: { displayId: string; id: string } | null;
  onName_enChange?: (value: string) => void;
  name_enError?: string;
  name_pt: string;
  name_ptSelectedValue?: { displayId: string; id: string } | null;
  onName_ptChange?: (value: string) => void;
  name_ptError?: string;
  monthlyCredits: number;
  monthlyCreditsSelectedValue?: { displayId: string; id: string } | null;
  onMonthlyCreditsChange?: (value: number) => void;
  monthlyCreditsError?: string;
  pricePerCredit: number;
  pricePerCreditSelectedValue?: { displayId: string; id: string } | null;
  onPricePerCreditChange?: (value: number) => void;
  pricePerCreditError?: string;
  pricePerExtraCredit: number;
  pricePerExtraCreditSelectedValue?: { displayId: string; id: string } | null;
  onPricePerExtraCreditChange?: (value: number) => void;
  pricePerExtraCreditError?: string;
  description_es: string;
  description_esSelectedValue?: { displayId: string; id: string } | null;
  onDescription_esChange?: (value: string) => void;
  description_esError?: string;
  description_en: string;
  description_enSelectedValue?: { displayId: string; id: string } | null;
  onDescription_enChange?: (value: string) => void;
  description_enError?: string;
  description_pt: string;
  description_ptSelectedValue?: { displayId: string; id: string } | null;
  onDescription_ptChange?: (value: string) => void;
  description_ptError?: string;
  creditTierUpdateStore: CreditTierUpdateStore;
};

export const CreditTierUpdateForm: React.FC<CreditTierUpdateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      name_es = "",
      name_esError,
      onName_esChange = () => {},
      name_en = "",
      name_enError,
      onName_enChange = () => {},
      name_pt = "",
      name_ptError,
      onName_ptChange = () => {},
      monthlyCredits = 0,
      monthlyCreditsError,
      onMonthlyCreditsChange = () => {},
      pricePerCredit = 0,
      pricePerCreditError,
      onPricePerCreditChange = () => {},
      pricePerExtraCredit = 0,
      pricePerExtraCreditError,
      onPricePerExtraCreditChange = () => {},
      description_es = "",
      description_esError,
      onDescription_esChange = () => {},
      description_en = "",
      description_enError,
      onDescription_enChange = () => {},
      description_pt = "",
      description_ptError,
      onDescription_ptChange = () => {},
      creditTierUpdateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelUpdatedEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="CreditTier"
              mode="update"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "name_es":
                    onName_esChange?.(valor);
                    break;
                  case "name_en":
                    onName_enChange?.(valor);
                    break;
                  case "name_pt":
                    onName_ptChange?.(valor);
                    break;
                  case "monthlyCredits":
                    onMonthlyCreditsChange?.(valor);
                    break;
                  case "pricePerCredit":
                    onPricePerCreditChange?.(valor);
                    break;
                  case "pricePerExtraCredit":
                    onPricePerExtraCreditChange?.(valor);
                    break;
                  case "description_es":
                    onDescription_esChange?.(valor);
                    break;
                  case "description_en":
                    onDescription_enChange?.(valor);
                    break;
                  case "description_pt":
                    onDescription_ptChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={creditTierUpdateStore.getCardErrors("null")}
              title={langStore.groupCreditTierLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputString
                  autoFocus
                  value={name_es}
                  error={Boolean(name_esError)}
                  warning={
                    !name_esError &&
                    Boolean(creditTierUpdateStore.warnings["name_es"])
                  }
                  helperText={
                    name_esError ||
                    creditTierUpdateStore.warnings["name_es"]?.message
                  }
                  label={langStore.CreditTierNameLabel + " (es)"}
                  onChange={(value: string) => onName_esChange(value)}
                  description={langStore.CreditTierNameDescription}
                />
                <InputString
                  value={name_en}
                  error={Boolean(name_enError)}
                  warning={
                    !name_enError &&
                    Boolean(creditTierUpdateStore.warnings["name_en"])
                  }
                  helperText={
                    name_enError ||
                    creditTierUpdateStore.warnings["name_en"]?.message
                  }
                  label={langStore.CreditTierNameLabel + " (en)"}
                  onChange={(value: string) => onName_enChange(value)}
                  description={langStore.CreditTierNameDescription}
                />
                <InputString
                  value={name_pt}
                  error={Boolean(name_ptError)}
                  warning={
                    !name_ptError &&
                    Boolean(creditTierUpdateStore.warnings["name_pt"])
                  }
                  helperText={
                    name_ptError ||
                    creditTierUpdateStore.warnings["name_pt"]?.message
                  }
                  label={langStore.CreditTierNameLabel + " (pt)"}
                  onChange={(value: string) => onName_ptChange(value)}
                  description={langStore.CreditTierNameDescription}
                />
                <InputNumber
                  value={monthlyCredits}
                  error={Boolean(monthlyCreditsError)}
                  warning={
                    !monthlyCreditsError &&
                    Boolean(creditTierUpdateStore.warnings["monthlyCredits"])
                  }
                  helperText={
                    monthlyCreditsError ||
                    creditTierUpdateStore.warnings["monthlyCredits"]?.message
                  }
                  label={langStore.CreditTierMonthlyCreditsLabel}
                  decimalScale={0}
                  description={langStore.CreditTierMonthlyCreditsDescription}
                  onChange={(value: number) => onMonthlyCreditsChange(value)}
                />
                <InputNumber
                  value={pricePerCredit}
                  error={Boolean(pricePerCreditError)}
                  warning={
                    !pricePerCreditError &&
                    Boolean(creditTierUpdateStore.warnings["pricePerCredit"])
                  }
                  helperText={
                    pricePerCreditError ||
                    creditTierUpdateStore.warnings["pricePerCredit"]?.message
                  }
                  label={langStore.CreditTierPricePerCreditLabel}
                  decimalScale={2}
                  description={langStore.CreditTierPricePerCreditDescription}
                  onChange={(value: number) => onPricePerCreditChange(value)}
                />
                <InputNumber
                  value={pricePerExtraCredit}
                  error={Boolean(pricePerExtraCreditError)}
                  warning={
                    !pricePerExtraCreditError &&
                    Boolean(
                      creditTierUpdateStore.warnings["pricePerExtraCredit"]
                    )
                  }
                  helperText={
                    pricePerExtraCreditError ||
                    creditTierUpdateStore.warnings["pricePerExtraCredit"]
                      ?.message
                  }
                  label={langStore.CreditTierPricePerExtraCreditLabel}
                  decimalScale={2}
                  description={
                    langStore.CreditTierPricePerExtraCreditDescription
                  }
                  onChange={(value: number) =>
                    onPricePerExtraCreditChange(value)
                  }
                />
                <InputString
                  value={description_es}
                  error={Boolean(description_esError)}
                  warning={
                    !description_esError &&
                    Boolean(creditTierUpdateStore.warnings["description_es"])
                  }
                  helperText={
                    description_esError ||
                    creditTierUpdateStore.warnings["description_es"]?.message
                  }
                  label={langStore.CreditTierDescriptionLabel + " (es)"}
                  onChange={(value: string) => onDescription_esChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
                <InputString
                  value={description_en}
                  error={Boolean(description_enError)}
                  warning={
                    !description_enError &&
                    Boolean(creditTierUpdateStore.warnings["description_en"])
                  }
                  helperText={
                    description_enError ||
                    creditTierUpdateStore.warnings["description_en"]?.message
                  }
                  label={langStore.CreditTierDescriptionLabel + " (en)"}
                  onChange={(value: string) => onDescription_enChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
                <InputString
                  value={description_pt}
                  error={Boolean(description_ptError)}
                  warning={
                    !description_ptError &&
                    Boolean(creditTierUpdateStore.warnings["description_pt"])
                  }
                  helperText={
                    description_ptError ||
                    creditTierUpdateStore.warnings["description_pt"]?.message
                  }
                  label={langStore.CreditTierDescriptionLabel + " (pt)"}
                  onChange={(value: string) => onDescription_ptChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
